import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useUsedFontFamiliesQuery } from '@/api/hooks/use-font-families'
import { getFontFamiliesAll } from '@/api/services/font-family.service'
import { queryKeys } from '@/api/query-keys'
import {
  buildFontFamilyIdToFamilyName,
  buildFontFamilyNameToId,
  buildFontIdByVariantLookup,
  buildFontIdLookup,
  findFontFamilyById,
  loadFontFacesForFamilies,
  normalizeFontFamilyKey,
  pickDefaultFontIdForFamily,
} from '@/lib/fonts/font-loader'
import { FontContext, type FontContextValue } from './font-context'
import { RecentFontFamiliesProvider } from '@/contexts/recent-font-families-context'
import type { FontFamily } from '@/api/types/font-family'

export type FontProviderProps = {
  children: React.ReactNode
  /** Passed as `?spreadId=…` to the font families endpoint. */
  spreadId?: string
}

export function FontProvider({ children, spreadId }: FontProviderProps) {
  const queryClient = useQueryClient()
  const fontQuery = useUsedFontFamiliesQuery(spreadId)

  const usedFamilies = useMemo(() => fontQuery.data ?? [], [fontQuery.data])
  /** Same source as `usedFamilies` (permissible list only; no separate full catalog). */
  const allFamilies = usedFamilies
  const families = usedFamilies

  const [failedFontFamilyNames, setFailedFontFamilyNames] = useState<ReadonlySet<string>>(new Set())
  const [facesLoadedGeneration, setFacesLoadedGeneration] = useState(0)

  /** Bump the invalidation counter only when faces genuinely landed — see
   *  `FontContextValue.facesLoadedGeneration`. Guarded on the count so the N `TextRenderer`s that
   *  each call `ensureFamilyFacesLoaded` for an already-cached family cause one render, not N. */
  const noteFacesLoaded = useCallback((loadedCount: number) => {
    if (loadedCount <= 0) return

    setFacesLoadedGeneration((n) => n + 1)
  }, [])

  const addFailedFamilyNames = useCallback((names: string[]) => {
    if (names.length === 0) return

    setFailedFontFamilyNames((prev) => {
      const next = new Set(prev)
      let added = false

      for (const name of names) {
        if (!next.has(name)) {
          next.add(name)
          added = true
        }
      }

      return added ? next : prev
    })
  }, [])

  const fontIdLookup = useMemo(() => buildFontIdLookup(families), [families])
  const fontIdByVariantLookup = useMemo(() => buildFontIdByVariantLookup(families), [families])
  const familyIdToName = useMemo(() => buildFontFamilyIdToFamilyName(families), [families])
  const familyNameToId = useMemo(() => buildFontFamilyNameToId(families), [families])

  const loadFullCatalog = useCallback(() => {
    /* No-op: permissible fonts are loaded with `useUsedFontFamiliesQuery`; there is no separate catalog. */
  }, [])

  const getFamilyById = useCallback(
    (fontFamilyId: number) => findFontFamilyById(families, fontFamilyId),
    [families]
  )

  const getVariantByFontId = useCallback(
    (fontId: number | undefined) => {
      if (fontId == null) return undefined

      return fontIdLookup.get(fontId)
    },
    [fontIdLookup]
  )

  const getFamilyCssName = useCallback(
    (fontFamilyId: number | undefined) => {
      if (fontFamilyId == null) return undefined
      const cssName = familyIdToName.get(fontFamilyId)

      if (!cssName || failedFontFamilyNames.has(cssName)) return undefined

      return cssName
    },
    [familyIdToName, failedFontFamilyNames]
  )

  const getFontIdByVariant = useCallback(
    (fontFamilyId: number, bold: boolean, italic: boolean) =>
      fontIdByVariantLookup.get(`${fontFamilyId}:${bold}:${italic}`),
    [fontIdByVariantLookup]
  )

  const getFamilyIdByCssName = useCallback(
    (cssName: string) => familyNameToId.get(normalizeFontFamilyKey(cssName)),
    [familyNameToId]
  )

  const pickDefaultFontIdForFamilyId = useCallback(
    (fontFamilyId: number) =>
      pickDefaultFontIdForFamily(findFontFamilyById(families, fontFamilyId)),
    [families]
  )

  useEffect(() => {
    if (usedFamilies.length === 0) return

    void loadFontFacesForFamilies(usedFamilies, {}).then(({ failedFonts, loadedCount }) => {
      addFailedFamilyNames(failedFonts.map((f) => f.familyName))
      noteFacesLoaded(loadedCount)
    })
  }, [usedFamilies, addFailedFamilyNames, noteFacesLoaded])

  const ensureFamilyFacesLoaded = useCallback(
    async (fontFamilyId: number) => {
      let list: FontFamily[] = usedFamilies
      let fam = findFontFamilyById(list, fontFamilyId)

      if (!fam && spreadId && !fontQuery.isError) {
        const payload = await queryClient.ensureQueryData({
          queryKey: queryKeys.fontFamilies.used(spreadId),
          queryFn: async () => (await getFontFamiliesAll(spreadId)).payload,
        })

        list = payload
        fam = findFontFamilyById(list, fontFamilyId)
      }

      if (!fam) return

      const { failedFonts, loadedCount } = await loadFontFacesForFamilies([fam], {})

      addFailedFamilyNames(failedFonts.map((f) => f.familyName))
      noteFacesLoaded(loadedCount)
    },
    [queryClient, spreadId, usedFamilies, addFailedFamilyNames, noteFacesLoaded, fontQuery.isError]
  )

  const isFamilyFailed = useCallback(
    (fontFamilyId: number) => {
      const cssName = familyIdToName.get(fontFamilyId)

      return cssName != null && failedFontFamilyNames.has(cssName)
    },
    [familyIdToName, failedFontFamilyNames]
  )

  const { error: fontQueryError, isError } = fontQuery
  const err = fontQueryError as Error | null

  const value = useMemo<FontContextValue>(
    () => ({
      usedFamilies,
      allFamilies,
      families,
      isUsedCatalogLoading: fontQuery.isLoading && !fontQuery.isFetched,
      isUsedCatalogReady: fontQuery.isSuccess,
      isFullCatalogLoading: false,
      isError,
      error: isError ? err : null,
      failedFontFamilyNames,
      facesLoadedGeneration,
      loadFullCatalog,
      ensureFamilyFacesLoaded,
      getFamilyById,
      getVariantByFontId,
      getFamilyCssName,
      getFontIdByVariant,
      getFamilyIdByCssName,
      pickDefaultFontIdForFamilyId,
      isFamilyFailed,
    }),
    [
      usedFamilies,
      allFamilies,
      families,
      fontQuery.isLoading,
      fontQuery.isFetched,
      fontQuery.isSuccess,
      isError,
      err,
      failedFontFamilyNames,
      facesLoadedGeneration,
      loadFullCatalog,
      ensureFamilyFacesLoaded,
      getFamilyById,
      getVariantByFontId,
      getFamilyCssName,
      getFontIdByVariant,
      getFamilyIdByCssName,
      pickDefaultFontIdForFamilyId,
      isFamilyFailed,
    ]
  )

  return (
    <FontContext.Provider value={value}>
      <RecentFontFamiliesProvider>{children}</RecentFontFamiliesProvider>
    </FontContext.Provider>
  )
}
